import axios from "axios";
import Cookies from "js-cookie";

interface FileMetadata {
  fileName: string;
  fileType: string;
  fileSize: number;
}

interface PresignedUploadEntry {
  fileName?: string;
  uploadUrl: string;
  fileUrl?: string;
  fileKey?: string;
}

export interface UploadableFileInput {
  file: File;
  description?: string;
  photoCategory?: string | null;
  [key: string]: unknown;
}

export interface UploadedPresignedFilePayload {
  fileName: string;
  fileKey?: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  description?: string;
  photoCategory?: string | null;
  [key: string]: unknown;
}

export interface PresignedUploadProgress {
  percentage: number;
  loadedBytes: number;
  totalBytes: number;
  completedFiles: number;
  totalFiles: number;
  activeFileName?: string;
}

interface PresignedUploadOptions {
  siteId?: number | string;
  folderName?: string;
  onProgress?: (progress: PresignedUploadProgress) => void;
}

const getApiBaseUrl = () => `${process.env.REACT_APP_API_URL}/v1`;

const getAuthHeaders = () => {
  const token = Cookies.get("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const normalizeGenerateResponse = (responseData: any, files: FileMetadata[]): PresignedUploadEntry[] => {
  const dataRoot = responseData?.data ?? responseData;
  const candidates = [
    dataRoot?.files,
    dataRoot?.urls,
    dataRoot?.presignedUrls,
    dataRoot?.presigned_urls,
    dataRoot,
  ];

  let rawEntries: any[] = [];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      rawEntries = candidate;
      break;
    }
  }

  if (!rawEntries.length) {
    throw new Error("Invalid generate response: no presigned URL entries found");
  }

  const normalizedEntries = rawEntries.map((entry, index) => ({
    fileName: entry?.fileName || entry?.file_name || entry?.name || files[index]?.fileName,
    uploadUrl: entry?.uploadUrl || entry?.upload_url || entry?.url || "",
    fileUrl: entry?.fileUrl || entry?.file_url || entry?.publicUrl || entry?.public_url || "",
    fileKey: entry?.fileKey || entry?.file_key || entry?.key || "",
  }));

  const aligned = files.map((file, index) => {
    const matchedByName = normalizedEntries.find((entry) => entry.fileName === file.fileName);
    return matchedByName || normalizedEntries[index];
  });

  if (aligned.some((entry) => !entry?.uploadUrl)) {
    throw new Error("Invalid generate response: missing upload URL");
  }

  return aligned as PresignedUploadEntry[];
};

const stripQueryParams = (url: string) => {
  const queryIndex = url.indexOf("?");
  return queryIndex === -1 ? url : url.substring(0, queryIndex);
};

const uploadToS3WithProgress = (
  file: File,
  uploadUrl: string,
  onProgress?: (loaded: number, total: number) => void
) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open("PUT", uploadUrl, true);
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress?.(event.loaded, event.total);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(file.size, file.size);
        resolve();
        return;
      }
      reject(new Error(`Failed to upload file ${file.name} to storage`));
    };

    xhr.onerror = () => reject(new Error(`Failed to upload file ${file.name} to storage`));
    xhr.send(file);
  });

const extractVerifiedUrlMap = (responseData: any): Map<string, string> => {
  const dataRoot = responseData?.data ?? responseData;
  const candidates = [
    dataRoot?.files,
    dataRoot?.urls,
    dataRoot?.verifiedFiles,
    dataRoot?.verified_files,
    dataRoot?.results,
    dataRoot,
  ];

  let rawEntries: any[] = [];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      rawEntries = candidate;
      break;
    }
  }

  const verifiedUrlMap = new Map<string, string>();
  for (const entry of rawEntries) {
    const key = entry?.fileKey || entry?.file_key || entry?.key || "";
    const url = entry?.fileUrl || entry?.file_url || entry?.publicUrl || entry?.public_url || entry?.url || "";
    if (key && url) {
      verifiedUrlMap.set(key, url);
    }
  }

  return verifiedUrlMap;
};

const normalizeFileInput = (input: File | UploadableFileInput): UploadableFileInput => {
  if (input instanceof File) {
    return { file: input };
  }
  return input;
};

export const uploadFilesWithPresignedPayload = async (
  inputs: Array<File | UploadableFileInput>,
  options: PresignedUploadOptions = {}
): Promise<UploadedPresignedFilePayload[]> => {
  if (!inputs.length) {
    return [];
  }

  const normalizedInputs = inputs.map(normalizeFileInput);
  const files = normalizedInputs.map((input) => input.file);
  const fileMetadata: FileMetadata[] = files.map((file) => ({
    fileName: file.name,
    fileType: file.type || "application/octet-stream",
    fileSize: file.size,
  }));

  const requestBody = {
    files: fileMetadata,
    ...(options.folderName ? { folderName: options.folderName } : {}),
  };
  const requestConfig = {
    params: options.siteId ? { site_id: options.siteId } : undefined,
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json",
    },
  };

  let generateResponse;
  try {
    generateResponse = await axios.post(
      `${getApiBaseUrl()}/presigned-urls/generate`,
      requestBody,
      requestConfig
    );
  } catch {
    generateResponse = await axios.post(
      `${getApiBaseUrl()}/presigned-urls/generate`,
      fileMetadata,
      requestConfig
    );
  }

  const presignedEntries = normalizeGenerateResponse(generateResponse.data, fileMetadata);
  const fileProgress = files.map(() => 0);
  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  const emitProgress = (activeFileName?: string) => {
    if (!options.onProgress) return;
    const loadedBytes = fileProgress.reduce((sum, loaded) => sum + loaded, 0);
    const completedFiles = fileProgress.filter((loaded, index) => loaded >= files[index].size).length;
    options.onProgress({
      percentage: totalBytes > 0 ? Math.min(100, Math.round((loadedBytes / totalBytes) * 100)) : 100,
      loadedBytes,
      totalBytes,
      completedFiles,
      totalFiles: files.length,
      activeFileName,
    });
  };

  emitProgress();

  await Promise.all(
    presignedEntries.map((entry, index) =>
      uploadToS3WithProgress(files[index], entry.uploadUrl, (loaded) => {
        fileProgress[index] = loaded;
        emitProgress(files[index].name);
      })
    )
  );

  const fileKeys = presignedEntries
    .map((entry) => entry.fileKey)
    .filter((key): key is string => typeof key === "string" && !!key);

  let verifiedUrlMap = new Map<string, string>();
  if (fileKeys.length) {
    try {
      const verifyResponse = await axios.post(
        `${getApiBaseUrl()}/presigned-urls/verify`,
        { fileKeys },
        {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
        }
      );
      verifiedUrlMap = extractVerifiedUrlMap(verifyResponse.data);
    } catch {
      try {
        const verifyResponse = await axios.post(
          `${getApiBaseUrl()}/presigned-urls/verify`,
          { file_keys: fileKeys },
          {
            headers: {
              ...getAuthHeaders(),
              "Content-Type": "application/json",
            },
          }
        );
        verifiedUrlMap = extractVerifiedUrlMap(verifyResponse.data);
      } catch {
        // Verify API is optional for this flow.
      }
    }
  }

  emitProgress();

  return presignedEntries.map((entry, index) => {
    const input = normalizedInputs[index];
    const file = input.file;
    const extraFields = Object.fromEntries(
      Object.entries(input).filter(([key]) => key !== "file" && input[key] !== undefined)
    );

    return {
      fileName: file.name,
      fileKey: entry.fileKey,
      fileUrl: entry.fileKey && verifiedUrlMap.has(entry.fileKey)
        ? (verifiedUrlMap.get(entry.fileKey) as string)
        : entry.fileUrl || stripQueryParams(entry.uploadUrl),
      fileSize: file.size,
      mimeType: file.type || "application/octet-stream",
      ...extraFields,
    };
  });
};

export const uploadFilesWithPresignedUrls = async (files: File[]): Promise<string[]> => {
  const uploadedFiles = await uploadFilesWithPresignedPayload(files);
  return uploadedFiles.map((file) => file.fileUrl);
};
